import React from 'react';
import styled from 'styled-components';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Label = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 4px;
`;

const CheckboxGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const CheckboxLabel = styled.label<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.$disabled ? 0.6 : 1)};

  &:hover {
    border-color: ${(p) => (p.$disabled ? '#e2e8f0' : '#3b82f6')};
    background: ${(p) => (p.$disabled ? '#f8fafc' : '#eff6ff')};
  }

  input {
    cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const SmallButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: #e2e8f0;
  color: #475569;

  &:hover {
    background: #cbd5e1;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export interface BuraxilisQuestionCheckboxGroupProps {
  availableQuestions: string[];
  selectedQuestionNumbers: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
  /** Optional label; default: "Sual seçin (sinif üzrə)" */
  label?: string;
}

const BuraxilisQuestionCheckboxGroup: React.FC<BuraxilisQuestionCheckboxGroupProps> = ({
  availableQuestions,
  selectedQuestionNumbers,
  onChange,
  disabled = false,
  label,
}) => {
  const selectedSet = new Set(selectedQuestionNumbers);

  const handleToggle = (q: string) => {
    if (disabled) return;
    if (selectedSet.has(q)) {
      onChange(selectedQuestionNumbers.filter((x) => x !== q));
    } else {
      onChange([...selectedQuestionNumbers, q].sort());
    }
  };

  const handleSelectAll = () => {
    if (disabled) return;
    onChange([...availableQuestions]);
  };

  const handleSelectNone = () => {
    if (disabled) return;
    onChange([]);
  };

  return (
    <Wrapper>
      <Label>{label ?? 'Sual seçin (sinif üzrə)'}</Label>
      <CheckboxGrid>
        {availableQuestions.map((q) => (
          <CheckboxLabel key={q} $disabled={disabled}>
            <input
              type="checkbox"
              checked={selectedSet.has(q)}
              onChange={() => handleToggle(q)}
              disabled={disabled}
            />
            {q}
          </CheckboxLabel>
        ))}
      </CheckboxGrid>
      <ButtonRow>
        <SmallButton type="button" onClick={handleSelectAll} disabled={disabled}>
          Hamısını seç
        </SmallButton>
        <SmallButton type="button" onClick={handleSelectNone} disabled={disabled}>
          Heç birini seç
        </SmallButton>
      </ButtonRow>
    </Wrapper>
  );
};

export default BuraxilisQuestionCheckboxGroup;
